import { toolboxApi, unwrapResult } from '~/modules/query/base';

/** One company row from GET /customers (companyid, name, ...). */
export interface OpsCustomer {
  [key: string]: unknown;
  companyid?: string | number;
}

/** GET /customers/agents/{companyID} — the agents the Social wizards and the page read. */
export interface CustomerAgent {
  [key: string]: unknown;
  publicid: string;
  firstname?: string;
  lastname?: string;
  email?: string;
  bay_account_id?: string | number | null;
  smc_login?: string;
}

export interface SaveAgentsChanges {
  listings?: string[];
  profiles?: string[];
  enableLogins?: string[];
  disableLogins?: string[];
}

type CompanyId = string | number;

const customerPath = (path: string) => `/customers${path}`;
const companyPath = (path: string, companyID: CompanyId) =>
  customerPath(`/${path}/${encodeURIComponent(companyID)}`);

export const customersApi = toolboxApi.injectEndpoints({
  endpoints: (builder) => ({
    getCustomers: builder.query<OpsCustomer[], void>({
      query: () => ({ url: customerPath('/'), method: 'GET' }),
      transformResponse: unwrapResult<OpsCustomer[]>,
      providesTags: ['Customer'],
    }),

    getCustomerAgents: builder.query<CustomerAgent[], CompanyId>({
      query: (companyID) => ({ url: companyPath('agents', companyID), method: 'GET' }),
      transformResponse: unwrapResult<CustomerAgent[]>,
      providesTags: ['CustomerAgents'],
    }),

    saveAgents: builder.mutation<unknown, { companyID: CompanyId } & SaveAgentsChanges>({
      query: ({
        companyID,
        listings = [],
        profiles = [],
        enableLogins = [],
        disableLogins = [],
      }) => ({
        url: companyPath('save-agents', companyID),
        method: 'POST',
        data: { listings, profiles, enableLogins, disableLogins },
      }),
      invalidatesTags: ['CustomerAgents'],
    }),

    saveParentAccountId: builder.mutation<
      unknown,
      { companyID: CompanyId; BAYParentAccountID: number | string }
    >({
      query: ({ companyID, BAYParentAccountID }) => ({
        url: customerPath('/save-parent-account-id'),
        method: 'PUT',
        data: { CompanyID: companyID, BAYParentAccountID },
      }),
      invalidatesTags: ['Customer'],
    }),

    // Sets SocialBay_Access = 'T' in the customer's own Settings table AND the parent account id.
    enableSocial: builder.mutation<
      unknown,
      { companyID: CompanyId; BAYParentAccountID: number | string }
    >({
      query: ({ companyID, BAYParentAccountID }) => ({
        url: customerPath('/enable-social'),
        method: 'PUT',
        data: { CompanyID: companyID, BAYParentAccountID },
      }),
      invalidatesTags: ['Customer', 'CustomerAgents'],
    }),

    // Enable Social wizard, step 4: set the chosen user's bay_account_id to the parent account id.
    setAgentBayAccountId: builder.mutation<
      unknown,
      { companyID: CompanyId; publicId: string; BAYAccountID: number | string }
    >({
      query: ({ companyID, publicId, BAYAccountID }) => ({
        url: customerPath('/agent-bay-account-id'),
        method: 'PUT',
        data: { CompanyID: companyID, publicId, BAYAccountID },
      }),
      invalidatesTags: ['CustomerAgents'],
    }),

    // Enable Social wizard, step 5: call office's sync.cfc sendAgent for the chosen user.
    sendAgent: builder.mutation<unknown, { companyID: CompanyId; publicId: string }>({
      query: ({ companyID, publicId }) => ({
        url: customerPath('/send-agent'),
        method: 'POST',
        data: { CompanyID: companyID, publicId },
      }),
      invalidatesTags: ['CustomerAgents'],
    }),
  }),
});

export const {
  useGetCustomersQuery,
  useGetCustomerAgentsQuery,
  useLazyGetCustomerAgentsQuery,
  useSaveAgentsMutation,
  useSaveParentAccountIdMutation,
  useEnableSocialMutation,
  useSetAgentBayAccountIdMutation,
  useSendAgentMutation,
} = customersApi;
